import { useCallback, useMemo, useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import { useTheme } from 'styled-components'

import { logger } from '../../..//utils/logger'
import useSortableData from '../../../application/hook/useSort'
import { useStore } from '../../../application/store/store'
import { useDocumentUseCase } from '../../../application/useCases/useDocuments'
import { Document } from '../../../domain/documents.types'
import { toastPromise } from '../../../infrastructure/hooks/useToast'
import { useCustomTranslation } from '../../../infrastructure/translation/useCustomTranslation'
import { filterData } from '../../components/table/table.helpers'
import { FilterProps, Header, SortConfig } from '../../components/table/table.types'

export const useDocumments = () => {
  const {
    documentsLoading,
    multipleDownloadLoading,
    documents,
    documentsError,
    selectedPlant,
    setBlobFetching,
    setSingleFileDownloading,
    setMultipleFilesDownloadLoading,
  } = useStore()
  const userAccessState = useStore((state) => state.user.access)
  const { downloadFile, downloadFiles, getBlob } = useDocumentUseCase()

  const initSortConfig: SortConfig = {
    key: 'displayName',
    direction: 'descending',
    method: 'alphabetical',
  }
  const [categoryFilterVisible, setCategoryFilterVisible] = useState(false)
  const [categorySortVisible, setCategorySortVisible] = useState(false)
  const [selectedItems, setSelectedItem] = useState<Document[]>([])
  const [filters, setFilters] = useState<FilterProps>({ key: 'categorie', filterValues: [] })
  const [isDownloading, setIsDownloading] = useState<boolean>(false)

  const { t } = useCustomTranslation('common')

  const headers: Header[] = [
    { value: t('type'), align: 'left', sort: 'alphabetical', propertyName: 'documentType' },
    { value: t('name'), align: 'left', sort: 'alphabetical', propertyName: 'displayName' },
    {
      value: t('plant'),
      align: 'left',
      sort: 'alphabetical',
      propertyName: 'powerPlantName',
    },
    {
      value: t('category'),
      align: 'center',
      sort: 'alphabetical',
      propertyName: 'reportType',
    },
    { value: t('date'), align: 'center', sort: 'date', propertyName: 'publishedAt' },
    { value: t('size'), align: 'center', sort: 'alphabetical', propertyName: 'size' },
    { value: t('preview'), align: 'center', sort: 'alphabetical', propertyName: 'none' },
    { value: t('download'), align: 'center', sort: 'alphabetical', propertyName: 'none' },
  ]

  const fetchBlob = async (file: Document) => {
    const id = file.sharepointId
    try {
      setIsDownloading(true)
      setBlobFetching(id)
      await toastPromise(getBlob(id), {
        pending: t('collect_your_document'),
        success: t('your_document_is_download'),
        error: t('error_collect_your_document'),
      })
      setBlobFetching(id)
      setIsDownloading(false)
    } catch (error) {
      setBlobFetching(id)
      logger.error(`documentsHook.fetchBlob - error: ${error}`)
    }
  }

  const downloadSingleFile = async (file: Document) => {
    try {
      setIsDownloading(true)
      setSingleFileDownloading(file.sharepointId)
      await toastPromise(downloadFile(file), {
        pending: t('collect_your_document'),
        success: t('your_document_is_download'),
        error: t('error_collect_your_document'),
      })
      setSingleFileDownloading(file.sharepointId)
      setIsDownloading(false)
    } catch (error) {
      setSingleFileDownloading(file.sharepointId)
      logger.error(`documentsHook.downloadSingleFile - error: ${error}`)
    }
  }

  const downloadMultipleFiles = async (documentsId: { [key: string]: boolean } | any) => {
    // type match tanstack table row selection formatting
    const sharepointIds = Object.keys(documentsId)
    try {
      setIsDownloading(true)
      setMultipleFilesDownloadLoading()
      await toastPromise(downloadFiles(sharepointIds), {
        pending: t('collect_your_documents'),
        success: t('your_documents_are_download'),
        error: t('error_collect_your_documents'),
      })
      setMultipleFilesDownloadLoading()
      setIsDownloading(false)
    } catch (error) {
      setMultipleFilesDownloadLoading()
      logger.error(`documentsHook.downloadMultipleFiles - error: ${error}`)
    }
  }

  // Ref to filter
  const wrapperRef = useRef(null)
  useClickAway(wrapperRef, () => {
    setCategoryFilterVisible(false)
    setCategorySortVisible(false)
  })
  const { isMobile } = useTheme()

  // MEMO AND HOOKS
  const memoizedFilteredData = useMemo(() => filterData(documents ?? [], filters), [
    documents,
    filters,
  ])

  const { items: sortedItems, requestSort, sortConfig: currentConfig } = useSortableData(
    memoizedFilteredData,
    initSortConfig
  )

  // Créer une Map pour une recherche O(1)
  const documentsMap = useMemo(
    () => new Map(documents?.map((doc) => [doc.sharepointId, doc]) || []),
    [documents]
  )

  const handleCategorySelect = (event: {
    target: { checked: boolean | undefined; value: string }
  }) => {
    const filtersCheckedValues = filters.filterValues
    const check = event.target.checked
    const checkedValue: string = event.target.value
    if (Array.isArray(filtersCheckedValues)) {
      if (check) {
        const newFilters = {
          key: filters.key,
          filterValues: [...filtersCheckedValues, checkedValue],
        }
        setFilters(newFilters)
      } else {
        const index = filtersCheckedValues.indexOf(checkedValue)
        if (index > -1) {
          filtersCheckedValues.splice(index, 1)
          setFilters({
            ...filters,
            filterValues: [...filtersCheckedValues],
          })
        }
      }
    }
  }

  // HANDLE CHECKBOX SELECTION
  const handleSelectItem = useCallback(
    (item: Record<string, any>, event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked
      setSelectedItem((prev) => {
        if (!isChecked) {
          return prev.filter((i) => i.sharepointId !== item.sharepointId)
        }

        const docSelected = documentsMap.get(item.sharepointId)
        if (!docSelected) {
          logger.error(`documentsHook.handleSelectItem - error: Document not found`)
          return prev
        }

        return [...prev, docSelected]
      })
    },
    [documentsMap]
  )

  return {
    t,
    documents,
    documentsError,
    handleSelectItem,
    documentsLoading,
    multipleDownloadLoading,
    selectedPlant,
    categoryFilterVisible,
    categorySortVisible,
    selectedItems,
    filters,
    fetchBlob,
    downloadSingleFile,
    downloadMultipleFiles,
    wrapperRef,
    isMobile,
    sortedItems,
    requestSort,
    currentConfig,
    handleCategorySelect,
    userAccessState,
    setCategorySortVisible,
    setCategoryFilterVisible,
    initSortConfig,
    headers,
    isDownloading,
    setIsDownloading
  }
}
